import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import {
  JewelleryBookPage,
  useJewelleryReportRange,
} from "@/components/reports/JewelleryBookPage";
import { mgToGrams, paiseToRupees } from "@/lib/jewellery-books-reports";
import {
  fetchDailyJewellerySummary,
  type DailyJewellerySummary,
} from "@/lib/jewellery-books-query";

export const Route = createFileRoute("/reports/daily-summary")({
  head: () => ({ meta: [{ title: "Daily Summary · AVS ERP" }] }),
  component: DailySummaryPage,
});

function DailySummaryPage() {
  const { period, setPeriod, customRange, setCustomRange, range } = useJewelleryReportRange();
  const [summary, setSummary] = useState<DailyJewellerySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchDailyJewellerySummary(range)
      .then((r) => {
        if (!cancelled) setSummary(r);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          console.error("[reports.daily-summary] Query failed:", e);
          setError(e.message || "Failed to load daily summary");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  useEffect(() => {
    const cleanup = loadData();
    return cleanup;
  }, [loadData]);

  const rows = summary
    ? [
        [
          `${summary.from} → ${summary.to}`,
          mgToGrams(summary.goldInMg),
          mgToGrams(summary.goldOutMg),
          paiseToRupees(summary.cashJamaPaise),
          paiseToRupees(summary.cashNavePaise),
          paiseToRupees(summary.invoiceTotalPaise),
          summary.invoiceCount,
        ],
        [
          "Purchases",
          mgToGrams(summary.purchaseFineMg),
          "",
          "",
          "",
          "",
          summary.purchaseCount,
        ],
      ]
    : [];

  return (
    <JewelleryBookPage
      title="Daily Summary"
      offlineName="Daily Summary"
      description="Period totals from server RPC (gold flow, cash Jama/Nave, sales, purchases)"
      columns={["Period", "Gold In (g)", "Gold Out (g)", "Cash Jama ₹", "Cash Nave ₹", "Sales ₹", "Count"]}
      rows={error || loading ? [] : rows}
      csvName="daily-summary.csv"
      period={period}
      onPeriodChange={setPeriod}
      customRange={customRange}
      onCustomRangeChange={setCustomRange}
      printHref={`/reports/book-print/daily_jewellery_summary?from=${range.from}&to=${range.to}`}
      loading={loading}
      error={error}
      onRetry={loadData}
      emptyMessage="No daily summary records found for the selected period."
    />
  );
}
